import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Section, Field, errToast } from "@/components/tax/shared";

export function PreparersPanel() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-tax-preparers"], queryFn: async () => (await api.get("/admin/tax/preparers")).data });
  const [f, setF] = useState({ email: "", display_name: "", firm_name: "", efile_number: "" });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-tax-preparers"] });
  const add = async () => {
    try { const r = (await api.post("/admin/tax/preparers", f)).data; toast.success(r.status === "assigned" ? "Preparer role assigned" : "Invite saved — role is granted when they register"); setF({ email: "", display_name: "", firm_name: "", efile_number: "" }); refresh(); } catch (e) { errToast(e); }
  };
  const revoke = async (id) => {
    if (!window.confirm("Revoke preparer access? They will lose access to all cases.")) return;
    try { await api.delete(`/admin/tax/preparers/${id}`); toast.success("Access revoked"); refresh(); } catch (e) { errToast(e); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="space-y-5" data-testid="admin-tax-preparers">
      <Section title="Add or invite a tax preparer" subtitle="Existing accounts get the tax_preparer role immediately; new emails are granted it on registration.">
        <div className="grid md:grid-cols-4 gap-3">
          <Field id="admin-prep-email" label="Email" value={f.email} onChange={set("email")} />
          <Field id="admin-prep-name" label="Display name" value={f.display_name} onChange={set("display_name")} />
          <Field id="admin-prep-firm" label="Firm" value={f.firm_name} onChange={set("firm_name")} />
          <Field id="admin-prep-efile" label="EFILE number (optional)" value={f.efile_number} onChange={set("efile_number")} />
        </div>
        <div className="flex justify-end mt-3"><Button data-testid="admin-prep-add-btn" className="bg-red-600 hover:bg-red-700 text-white rounded-full" onClick={add}>Add preparer</Button></div>
      </Section>
      <Section title="Preparers">
        {(data?.preparers || []).map((p) => (
          <div key={p.id} className="flex flex-wrap items-center gap-3 py-3 border-b border-slate-100 last:border-0" data-testid={`admin-prep-row-${p.id}`}>
            <div className="flex-1"><p className="font-semibold">{p.display_name || p.name} <span className="text-slate-500 font-normal">· {p.firm_name || "Independent"}</span></p><p className="text-xs text-slate-500">{p.email} · {p.cases} case(s){p.efile_number ? ` · EFILE #${p.efile_number}` : ""}</p></div>
            <span className={`text-xs font-semibold ${p.approved ? "text-emerald-700" : "text-slate-500"}`}>{p.approved ? "Approved" : "Not approved"}</span>
            <Button size="sm" variant="ghost" className="text-red-600" data-testid={`admin-prep-revoke-${p.id}`} onClick={() => revoke(p.id)}>Revoke</Button>
          </div>
        ))}
        {data && !data.preparers.length && <p className="text-sm text-slate-500">No preparers yet.</p>}
        {(data?.invites || []).map((i) => <p key={i.email} className="text-sm text-slate-600 py-1" data-testid="admin-prep-invite">Invited: {i.email} {i.firm_name ? `(${i.firm_name})` : ""}</p>)}
      </Section>
    </div>
  );
}

export function IntegrationsPanel() {
  const qc = useQueryClient();
  const { data: provs = [] } = useQuery({ queryKey: ["admin-tax-integrations"], queryFn: async () => (await api.get("/admin/tax/integrations")).data });
  const { data: errors = [] } = useQuery({ queryKey: ["admin-tax-errors"], queryFn: async () => (await api.get("/admin/tax/errors")).data });
  const toggle = async (p) => {
    try { await api.put(`/admin/tax/integrations/${p.key}`, { enabled: !p.enabled }); qc.invalidateQueries({ queryKey: ["admin-tax-integrations"] }); } catch (e) { errToast(e); }
  };
  const resolve = async (id) => { await api.post(`/admin/tax/errors/${id}/resolve`); qc.invalidateQueries({ queryKey: ["admin-tax-errors"] }); };
  return (
    <div className="space-y-5" data-testid="admin-tax-integrations">
      <Section title="Filing integrations" subtitle="Direct EFILE stays disabled until a certified software partner, supported integration and EFILE approval are confirmed.">
        {provs.map((p) => (
          <div key={p.key} className="flex flex-wrap items-center gap-3 py-3 border-b border-slate-100 last:border-0" data-testid={`admin-integration-${p.key}`}>
            <div className="flex-1 min-w-[240px]"><p className="font-semibold">{p.name}</p><p className="text-xs text-slate-500">{p.description}</p></div>
            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${p.operational ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{p.operational ? "Operational" : "Not connected"}</span>
            <Button size="sm" variant="outline" disabled={!p.operational} data-testid={`admin-integration-toggle-${p.key}`} onClick={() => toggle(p)}>{p.enabled ? "Disable" : "Enable"}</Button>
          </div>
        ))}
      </Section>
      <Section title="Errors" subtitle="Extraction and secure-storage failures. No personal data is logged.">
        {errors.length === 0 ? <p className="text-sm text-slate-500" data-testid="admin-tax-errors-empty">No errors.</p> : errors.map((e) => (
          <div key={e.id} className="flex items-center gap-3 py-2 border-b border-slate-100 text-sm" data-testid={`admin-tax-error-${e.id}`}>
            <span className="text-xs font-mono bg-slate-100 px-2 py-0.5 rounded">{e.kind}</span>
            <span className="flex-1">{e.message}</span>
            <span className="text-xs text-slate-500">{new Date(e.at).toLocaleString()}</span>
            {e.resolved ? <span className="text-xs text-emerald-700">Resolved</span> : <Button size="sm" variant="ghost" onClick={() => resolve(e.id)}>Resolve</Button>}
          </div>
        ))}
      </Section>
    </div>
  );
}
